import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Card, Chip, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LaunchIcon from "@mui/icons-material/Launch";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import Grid from "../Shared/Grid/Grid";
import Slider from "../Shared/Widgets/Slider/Slider";
import Awardee from "../Awardee/Awardees";
import UtaProfileLink from "../Shared/UtaProfileLink/UtaProfileLink";
import Loader from "../Shared/Widgets/Loader/Loader";
import {
  useGetAwardByIdQuery,
  useLazyGetAwardeeByIdQuery,
  useLazyGetAwardeeByIdNameQuery,
  useLazyGetAwardeGraphByIdQuery,
  useLazyGetAwardGenderComputeQuery,
} from "../../services/rtkQueryServices/checkApiService";
import { useThemeMode } from "../../theme/ThemeContext";
import NAM from "../../assets/images/NAM.png";
import NAE from "../../assets/images/NAE.png";
import AAS from "../../assets/images/AAS.png";
import NAS from "../../assets/images/NAS.jpg";
import NAI from "../../assets/images/NAI.jpeg";
import { academyFlags } from "../../utils/academies";
import "./AwardsSearch.scss";

const YEARS_START = 1981;
const YEARS_END_EXCL = new Date().getFullYear();
const clean = (v) => (typeof v === "string" ? v.replace(/\r/g, " ").trim() : v);
const externalHref = (url) =>
  !url ? null : /^https?:\/\//i.test(url) ? url : `https://${url}`;

const ACADEMY_LEGEND = [
  { src: NAS, key: "NAS", name: "National Academy of Sciences", url: "https://www.nasonline.org" },
  { src: NAE, key: "NAE", name: "National Academy of Engineering", url: "https://www.nae.edu" },
  { src: NAM, key: "NAM", name: "National Academy of Medicine", url: "https://nam.edu" },
  { src: NAI, key: "NAI", name: "National Academy of Inventors", url: "https://academyofinventors.org" },
  { src: AAS, key: "AAS", name: "American Academy of Arts & Sciences", url: "https://www.amacad.org" },
];

// Hover legend for the eye icon in the "Academies" column header.
const academyLegendTip = (
  <div className="academy-legend-tip">
    {ACADEMY_LEGEND.map((a) => (
      <a
        key={a.key}
        className="academy-legend-tip-row"
        href={a.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={a.src} alt="" />
        <b>{a.key}</b>
        <span>{a.name}</span>
      </a>
    ))}
  </div>
);

export default function AwardDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isUta } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const hint = location.state?.currentAwardData;

  // Authoritative fetch (refresh-safe); instant-paint from the row-click hint.
  const { data: byId } = useGetAwardByIdQuery(id, { skip: !id });
  const raw = byId?.data?.[0];

  // Normalize the two shapes (remapped row hint vs raw DB columns) into one.
  const award = useMemo(
    () => ({
      id: Number(id),
      name: hint?.awardName ?? raw?.Award ?? "",
      website: hint?.website ?? raw?.Website,
      organization: hint?.organization ?? raw?.Organization,
      category: hint?.category ?? clean(raw?.Category),
      level: hint?.level ?? clean(raw?.Level),
      kind:
        hint?.awardkind ??
        (raw?.Amount === "Unspecified" ? "Recognition" : raw?.Amount),
      notes: hint?.awardNotes ?? raw?.Notes,
      percentageWomen: hint?.percentageWomen ?? raw?.PercentageWomen,
      firstYearRecorded: hint?.firstYearRecorded ?? raw?.firstYearRecorded,
      aauPrestigious:
        hint?.aauPrestigious ?? raw?.["!AAU_HighlyPrestigious"] === "checked",
    }),
    [id, hint, raw]
  );

  const [awardees, setAwardees] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingAwardees, setLoadingAwardees] = useState(false);
  const [awardeesError, setAwardeesError] = useState(false);
  const [fetchAwardees] = useLazyGetAwardeeByIdNameQuery();
  const [
    fetchGraph,
    { data: awardGraphData, isFetching: graphFetching, isUninitialized: graphUninit },
  ] = useLazyGetAwardeGraphByIdQuery();
  const [fetchAwardee, { data: awardeeData }] = useLazyGetAwardeeByIdQuery();
  const [openSlider, setOpenSlider] = useState(false);
  const [sliderTitle, setSliderTitle] = useState("");
  const [sliderUsername, setSliderUsername] = useState(null);

  // Async gender fallback: computed live from awardees.sex only when neither the
  // year-by-year bars nor the pie has data. Kept out of the initial page load.
  const [fetchGenderCompute] = useLazyGetAwardGenderComputeQuery();
  const [computed, setComputed] = useState(null);
  const [computing, setComputing] = useState(false);
  const [computeErr, setComputeErr] = useState(false);
  const computeTriggered = useRef();
  // Set once THIS award's roster has loaded, so the fallback only decides on
  // fresh (current-id) data, not the previous award's or an empty initial state.
  const [loadedId, setLoadedId] = useState(null);

  useEffect(() => {
    if (!id) return;
    setLoadingAwardees(true);
    setAwardeesError(false);
    fetchAwardees(id)
      .unwrap()
      .then((result) => {
        const data = result?.data || [];
        setAwardees(data);
        const total = result?.totalRecords || data.length;
        setTotalRecords(total);
        if (total > 200) fetchGraph(id);
        setLoadedId(id); // roster for this award is settled; safe to decide the chart
      })
      .catch(() => setAwardeesError(true))
      .finally(() => setLoadingAwardees(false));
  }, [id, fetchAwardees, fetchGraph]);

  useEffect(() => {
    const val = openSlider ? "hidden" : "auto";
    document.body.style.overflow = val;
    document.documentElement.style.overflow = val;
    return () => {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, [openSlider]);

  const href = externalHref(award.website);

  // ── Gender chart (ported verbatim): year-by-year bars when coverage is rich,
  // else an overall male:female pie. ──
  const graphInputData = useMemo(() => {
    if (!(totalRecords > 200) || !awardGraphData?.data?.length) return [];
    const years = Array.from(
      { length: YEARS_END_EXCL - YEARS_START },
      (_, k) => YEARS_START + k
    );
    const acc = years.map((year) => ({ year, m: 0, f: 0 }));
    for (const row of awardGraphData.data) {
      for (let i = 0; i < years.length; i++) {
        const y = years[i];
        acc[i].m += Number(row[`gcm_${y}`] || 0);
        acc[i].f += Number(row[`gcw_${y}`] || 0);
      }
    }
    // Trim leading/trailing years with no recipients so the x-axis starts at
    // the first year this award actually has data (not a fixed 1981) and ends
    // at the last year with data.
    const first = acc.findIndex(({ m, f }) => m + f > 0);
    if (first === -1) return [];
    let last = acc.length - 1;
    while (last > first && acc[last].m + acc[last].f === 0) last--;
    return acc.slice(first, last + 1).map(({ year, m, f }) => {
      const tot = m + f;
      return { year, Male: tot ? (m / tot) * 100 : 0, Female: tot ? (f / tot) * 100 : 0 };
    });
  }, [totalRecords, awardGraphData]);

  // Null-safe: x-charts calls valueFormatter with null while resolving tooltips.
  const valueFormatter = (v) => (v == null ? "" : `${Number(v).toFixed(1)}%`);
  // Thin the year axis: show a label only every 5th year, but keep the full
  // year in the tooltip header (location-aware formatter).
  const yearTick = (year, ctx) =>
    ctx?.location === "tick"
      ? year % 5 === 0
        ? `'${String(year).slice(2)}`
        : ""
      : String(year);
  const chartSetting = {
    yAxis: [{ label: "Percentage (%)", min: 0, max: 100 }],
    xAxis: [
      {
        label: "Year",
        scaleType: "band",
        dataKey: "year",
        tickPlacement: "middle",
        valueFormatter: yearTick,
      },
    ],
    // Polished bars: rounded tops + a light horizontal grid. No fixed width, so
    // x-charts fills its container (height fixed) and stays responsive.
    borderRadius: 5,
    grid: { horizontal: true },
    height: 250,
    sx: {
      [`.${axisClasses.left} .${axisClasses.label}`]: { transform: "translate(-6px, 0)" },
    },
  };
  const barSeries = [
    { dataKey: "Male", label: "Male", stack: true, color: "var(--primary-color)", valueFormatter },
    { dataKey: "Female", label: "Female", stack: true, color: "var(--accent)", valueFormatter },
  ];

  const yearCoverage = useMemo(() => {
    if (!awardees?.length) return 1;
    const withYear = awardees.reduce((n, a) => {
      const y = a?.Year_of_Award;
      return n + (y !== null && y !== undefined && y !== "" && Number(y) > 0 ? 1 : 0);
    }, 0);
    return withYear / awardees.length;
  }, [awardees]);
  // Require real bar data too: a 200+ award with high year coverage but an empty
  // AwardYears row must NOT draw empty bars or suppress the compute fallback.
  const showByYear =
    totalRecords > 200 && yearCoverage > 0.95 && graphInputData.length > 0;

  const genderTotals = useMemo(() => {
    let male = 0;
    let female = 0;
    if (awardGraphData?.data?.length) {
      for (let y = YEARS_START; y < YEARS_END_EXCL; y++) {
        for (const row of awardGraphData.data) {
          male += Number(row[`gcm_${y}`] || 0);
          female += Number(row[`gcw_${y}`] || 0);
        }
      }
    }
    return { male, female, total: male + female };
  }, [awardGraphData]);

  const pieData = useMemo(() => {
    const pw = award.percentageWomen;
    let female;
    if (pw !== null && pw !== undefined && pw !== "") female = Number(pw);
    else if (genderTotals.total > 0) female = (genderTotals.female / genderTotals.total) * 100;
    else return [];
    return [
      { id: 0, value: 100 - female, label: "Male", color: "var(--primary-color)" },
      { id: 1, value: female, label: "Female", color: "var(--accent)" },
    ];
  }, [award.percentageWomen, genderTotals]);
  const hasPie = pieData.length > 0;

  // Reset the async fallback whenever the award changes.
  useEffect(() => {
    setComputed(null);
    setComputeErr(false);
    setComputing(false);
    computeTriggered.current = undefined;
  }, [id]);

  // Fire the live compute only when both primary paths came up empty. The rest
  // of the page is already rendered; only the chart area waits on this.
  useEffect(() => {
    if (!id || awardeesError) return;
    // Decide only once THIS award's roster has loaded, so totalRecords / hasPie /
    // showByYear reflect the current id (not the previous award or an empty init).
    if (loadedId !== id) return;
    // For 200+ awards the graph fetch must settle first, else we'd fire before
    // the pie fallback data (summed gcm/gcw) has a chance to arrive.
    const graphSettled = totalRecords <= 200 || (!graphUninit && !graphFetching);
    if (!graphSettled) return;
    if (showByYear || hasPie) return; // a primary path will render
    if (computeTriggered.current === id) return;
    const reqId = id;
    computeTriggered.current = reqId;
    setComputing(true);
    setComputeErr(false);
    fetchGenderCompute(reqId)
      .unwrap()
      .then((res) => {
        if (computeTriggered.current !== reqId) return; // superseded by a newer award
        if (res?.data) setComputed(res.data);
        else setComputeErr(true); // null/empty payload = failed or no usable data
      })
      .catch(() => {
        if (computeTriggered.current === reqId) setComputeErr(true);
      })
      .finally(() => {
        if (computeTriggered.current === reqId) setComputing(false);
      });
  }, [
    id,
    awardeesError,
    loadedId,
    totalRecords,
    graphUninit,
    graphFetching,
    showByYear,
    hasPie,
    fetchGenderCompute,
  ]);

  // Shape the computed result into either bars (enough years) or a pie.
  const computedChart = useMemo(() => {
    if (!computed) return null;
    const { byYear = [], male = 0, female = 0, withYear = 0, percentageWomen } = computed;
    const known = male + female;
    if (known === 0) return { kind: "none" }; // sex column empty for this award too
    // Coverage measured within the gendered population (withYear counts only
    // male/female rows), so the gate matches the bars that get plotted.
    const yearCov = known ? withYear / known : 0;
    if (byYear.length >= 2 && yearCov > 0.95) {
      let dp = 0; // datapoints = recipients with both a year and a male/female value
      const data = byYear.map(({ year, male: m, female: f }) => {
        dp += m + f;
        const tot = m + f;
        return { year, Male: tot ? (m / tot) * 100 : 0, Female: tot ? (f / tot) * 100 : 0 };
      });
      return { kind: "bars", data, datapoints: dp };
    }
    const femalePct =
      percentageWomen != null ? Number(percentageWomen) : (female / known) * 100;
    return {
      kind: "pie",
      datapoints: known, // recipients counted as male or female (excludes orgs/other)
      data: [
        { id: 0, value: 100 - femalePct, label: "Male", color: "var(--primary-color)" },
        { id: 1, value: femalePct, label: "Female", color: "var(--accent)" },
      ],
    };
  }, [computed]);

  const renderBars = (dataset) => (
    <BarChart dataset={dataset} series={barSeries} {...chartSetting} />
  );
  const renderPie = (data) => (
    <PieChart
      series={[
        {
          data,
          arcLabel: (item) => `${Math.round(item.value)}%`,
          arcLabelRadius: "65%",
          highlightScope: { faded: "global", highlighted: "item" },
        },
      ]}
      width={isMobile ? undefined : 380}
      height={isMobile ? 320 : 280}
      margin={
        isMobile
          ? { top: 16, right: 16, bottom: 56, left: 16 }
          : { top: 16, right: 150, bottom: 16, left: 16 }
      }
      slotProps={{
        legend: isMobile
          ? { direction: "row", position: { vertical: "bottom", horizontal: "middle" }, itemGap: 14 }
          : { direction: "column", position: { vertical: "middle", horizontal: "right" }, itemGap: 14 },
      }}
      sx={{ "& .MuiPieArcLabel-root": { fill: "#fff", fontWeight: 700, fontSize: "0.95rem" } }}
    />
  );

  // Primary paths first (AwardYears bars / PercentageWomen pie); if both are
  // empty, show a loader while the live compute runs, then render its result.
  let genderChart;
  if (showByYear) genderChart = renderBars(graphInputData);
  else if (hasPie) genderChart = renderPie(pieData);
  else if (computedChart?.kind === "bars") genderChart = renderBars(computedChart.data);
  else if (computedChart?.kind === "pie") genderChart = renderPie(computedChart.data);
  else if (computedChart?.kind === "none" || computeErr || awardeesError)
    genderChart = <div className="award-muted">Gender breakdown unavailable.</div>;
  else if (computing) genderChart = <Loader inline />;
  else genderChart = <Loader inline />; // settling before the async compute fires

  // ── Awardee roster grid (≤200) ──
  const awardeeRows = useMemo(
    () =>
      awardees.map((a, idx) => {
        // This endpoint serves raw `awardees` rows, so these are marker
        // characters, not 0/1. Decode via the shared helper - testing for a
        // specific character is what killed the NAM badge (see utils/academies).
        const flags = academyFlags(a);
        return {
          rowId: idx,
          year: a.Year_of_Award,
          fullName: a.FullName,
          ...flags,
          academyCount: Object.values(flags).filter(Boolean).length,
          isUTA: a.uta,
          utaUsername: a.utaUsername,
          id: a.RaiseProjectID,
        };
      }),
    [awardees]
  );

  const renderName = ({ value, data }) => (
    <UtaProfileLink username={data?.utaUsername}>
      <span className={isUta && data?.isUTA === "checked" ? "org-bg p-2" : ""}>{value}</span>
    </UtaProfileLink>
  );
  const renderAcademies = ({ data }) => {
    const badges = ACADEMY_LEGEND.filter((a) => data?.[a.key.toLowerCase()]);
    if (!badges.length) return <span className="award-muted">-</span>;
    return (
      <div className="awardee-academies">
        {badges.map((a) => (
          <a
            key={a.key}
            className="academy-link"
            href={a.url}
            target="_blank"
            rel="noopener noreferrer"
            title={a.name}
            // Don't open the awardee slider when the badge itself is clicked.
            onClick={(e) => e.stopPropagation()}
          >
            <img className="award-image" src={a.src} alt={a.key} title={a.name} />
          </a>
        ))}
      </div>
    );
  };
  const awardeeColumnDefs = [
    { headerName: "Year", field: "year", style: { minWidth: "80px" }, align: "center", sortable: true },
    { headerName: "Awardee", field: "fullName", style: { minWidth: "220px" }, sortable: true, cellRenderer: renderName },
    {
      headerName: "Academies",
      field: "academyCount",
      style: { minWidth: "140px" },
      sortable: true,
      cellRenderer: renderAcademies,
      headerCustomComponent: (
        <Tooltip title={academyLegendTip} arrow>
          <InfoOutlinedIcon
            sx={{
              fontSize: 16,
              verticalAlign: "-3px",
              ml: "6px",
              opacity: 0.85,
              cursor: "help",
            }}
          />
        </Tooltip>
      ),
    },
  ];

  // The UTA-faculty note is only meaningful when a highlighted row can appear.
  const hasUtaAwardee = awardeeRows.some((r) => r.isUTA === "checked");

  const handleAwardeeClick = (row) => {
    if (row.id) fetchAwardee(row.id);
    setSliderTitle(row.fullName);
    setSliderUsername(row.utaUsername || null);
    setOpenSlider(true);
  };

  const heroChips = [
    award.category && { label: award.category },
    award.level && { label: award.level },
    award.kind && { label: award.kind },
  ].filter(Boolean);

  // awardees.Society is a noisy denormalized copy of awards.Organization
  // (same org, inconsistent text), so only Organization is listed.
  const dossier = [
    award.organization && ["Organization", award.organization],
    award.category && ["Category", award.category],
    award.level && ["Level", award.level],
    award.kind && ["Kind", award.kind],
    award.firstYearRecorded && ["First year recorded", award.firstYearRecorded],
    ["Total awardees since 1981", totalRecords],
    award.percentageWomen != null &&
      award.percentageWomen !== "" && ["% women", `${award.percentageWomen}%`],
  ].filter(Boolean);

  return (
    <div className="award-detail">
      <Button
        className="award-back-btn"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() =>
          // Prefer history (restores scroll/state); fall back to the results
          // (with the carried search) when the detail page was deep-linked.
          location.key !== "default"
            ? navigate(-1)
            : navigate({ pathname: "/awards", search: location.search })
        }
      >
        Back to results
      </Button>

      {/* HERO */}
      <Box
        className="award-detail-hero"
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: "16px",
          p: { xs: 3, md: 4 },
          mb: 3,
          color: "#fff",
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          boxShadow: "0 10px 30px rgba(16,24,40,0.12)",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="overline" sx={{ letterSpacing: "0.18em", fontWeight: 700, color: "secondary.main" }}>
              {award.organization || "Award"}
            </Typography>
            {award.aauPrestigious && (
              <Chip
                size="small"
                color="secondary"
                icon={<WorkspacePremiumIcon />}
                label="Honorific"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, maxWidth: 820, lineHeight: 1.15 }}>
            {award.name}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ mb: href ? 2.5 : 0 }}>
            {heroChips.map((c, i) => (
              <Chip key={i} size="small" label={c.label}
                sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 600 }} />
            ))}
          </Stack>
          {href && (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              endIcon={<LaunchIcon />}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 700 }}
            >
              Award website
            </Button>
          )}
        </Box>
      </Box>

      {loadingAwardees ? (
        <Loader inline />
      ) : (
        <>
          {/* DOSSIER + CHART */}
          <div className="award-detail-grid">
            <Card variant="outlined" className="award-dossier">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, opacity: 0.7 }}>
                Details
              </Typography>
              <dl className="award-dossier-list">
                {dossier.map(([k, v]) => (
                  <div className="award-dossier-row" key={k}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
              {award.notes && <p className="award-dossier-notes">{award.notes}</p>}
              {/* Note only makes sense when a highlighted UTA row actually exists
                  in the roster; for 200+ awardees we link out instead, so hide it. */}
              {isUta && totalRecords <= 200 && hasUtaAwardee && (
                <div className="uta-faculty-note">
                  <span className="uta-faculty-note-dot" aria-hidden />
                  Highlighted individuals are UTA faculty.
                </div>
              )}
            </Card>

            <Card variant="outlined" className="award-chart-card">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, opacity: 0.7 }}>
                Gender representation
              </Typography>
              {award.percentageWomen != null &&
                award.percentageWomen !== "" &&
                award.firstYearRecorded && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Since {award.firstYearRecorded}, {award.percentageWomen}% of awardees are women.
                </Typography>
              )}
              <div className="award-chart-holder">{genderChart}</div>
              {computedChart &&
                (computedChart.kind === "bars" || computedChart.kind === "pie") && (
                  <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
                    Computed live from {computedChart.datapoints.toLocaleString()} recipient
                    {computedChart.datapoints === 1 ? "" : "s"} with a recorded gender
                    {computed?.total ? ` (of ${computed.total.toLocaleString()} total)` : ""}.
                  </Typography>
                )}
            </Card>
          </div>

          {/* AWARDEE ROSTER */}
          <Card variant="outlined" className="award-roster-card">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, opacity: 0.7 }}>
              Awardees ({totalRecords})
            </Typography>
            {awardeesError ? (
              <div className="award-muted" style={{ padding: "8px 0" }}>
                Couldn&apos;t load awardees. Please refresh to try again.
              </div>
            ) : totalRecords > 200 ? (
              <div className="award-roster-overflow">
                There are more than 200 awardees - see the full list on the{" "}
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="award-website-link">
                    award website ↗
                  </a>
                ) : (
                  "award website"
                )}
                .
              </div>
            ) : (
              <Grid
                columnDefs={awardeeColumnDefs}
                rowData={awardeeRows}
                masterDetail
                uniqueIdentifierFieldName={"rowId"}
                onRowClicked={handleAwardeeClick}
              />
            )}
          </Card>
        </>
      )}

      <Slider isOpen={openSlider} onClose={() => setOpenSlider(false)}>
        <Awardee
          awardeeData={awardeeData}
          sliderTitle={sliderTitle}
          utaUsername={sliderUsername}
          handleOnCloseClick={() => setOpenSlider(false)}
        />
      </Slider>
    </div>
  );
}
